/** Monday-based week bounds (YYYY-MM-DD) for a column anchor date. */
import { formatAppIntlLabel } from "./appDateTime";

export function getMondayStartOfWeekContaining(dateInput) {
  const parsed =
    typeof dateInput === "string"
      ? parseLocalDateInput(dateInput)
      : dateInput instanceof Date
        ? new Date(
            dateInput.getFullYear(),
            dateInput.getMonth(),
            dateInput.getDate(),
          )
        : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  const start = new Date(parsed);
  const diffToMonday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - diffToMonday);
  return start;
}

export function parseLocalDateInput(value) {
  if (!value) return null;
  const str = String(value).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function formatDateYmd(d) {
  const date = d instanceof Date ? d : parseLocalDateInput(d);
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Returns { shiftDateFrom, shiftDateTo, weekStart } for staffing-requests week filter. */
export function getScheduleWeekBounds(anchorDate) {
  const monday = getMondayStartOfWeekContaining(anchorDate);
  if (!monday) return { shiftDateFrom: "", shiftDateTo: "", weekStart: null };
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    shiftDateFrom: formatDateYmd(monday),
    shiftDateTo: formatDateYmd(sunday),
    weekStart: monday,
  };
}

export function formatWeekStartLabel(weekStart) {
  if (!weekStart) return "";
  return formatAppIntlLabel(weekStart, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function staffRequestsIncludeStorageKey(clientId, shiftDateFrom, shiftDateTo) {
  return `schedule-staff-requests-include:${clientId}:${shiftDateFrom || ""}:${shiftDateTo || ""}`;
}

/** Replicated request ids to keep visible for a client/week (shifts may fall outside the week). */
export function loadIncludedStaffRequestIds(
  clientId,
  shiftDateFrom,
  shiftDateTo,
) {
  if (!clientId) return [];
  try {
    const raw = sessionStorage.getItem(
      staffRequestsIncludeStorageKey(clientId, shiftDateFrom, shiftDateTo),
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return [
      ...new Set(
        parsed
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    ];
  } catch {
    return [];
  }
}

export function persistIncludedStaffRequestId(
  clientId,
  shiftDateFrom,
  shiftDateTo,
  staffRequestId,
) {
  const id = Number(staffRequestId);
  if (!clientId || !Number.isFinite(id) || id <= 0) return;
  const ids = new Set(
    loadIncludedStaffRequestIds(clientId, shiftDateFrom, shiftDateTo),
  );
  ids.add(id);
  try {
    sessionStorage.setItem(
      staffRequestsIncludeStorageKey(clientId, shiftDateFrom, shiftDateTo),
      JSON.stringify([...ids]),
    );
  } catch {
    /* ignore quota / private mode */
  }
}
